import axios from "axios";
import { toast } from "react-toastify";
import { clearWaitingAnswers } from "../components/chatbot/testShell/answerOutbox";
import { clearAllDrafts } from "../components/pages/appointments/notes/notesDraft";
import { turnOffPushOnSignOut } from "../pwa/pushSignOut";

// Determine baseURL dynamically based on the hostname
const getBaseURL = () => {
  const hostname = window.location.hostname;

  // Map domains to their corresponding backend URLs
  const tenantMapping = {
    "uic.annaguidance.ai": "https://api.uic.annaguidance.ai", // UIC tenant
    "ndm.annaguidance.ai": "https://api.ndm.annaguidance.ai", // NDM tenant
    "uicccs.annaguidance.ai": "https://api.uicccs.annaguidance.ai", // UIC(CCS) tenant
    "ndmi.annaguidance.ai": "https://api.ndmi.annaguidance.ai", // NDMI tenant
    "smak.annaguidance.ai": "https://api.smak.annaguidance.ai", // SMAK tenant
    "smct.annaguidance.ai": "https://api.smct.annaguidance.ai", // SMC tenant (new domain)
    "rmc.annaguidance.ai": "https://api.rmc.annaguidance.ai", // SMC tenant (new domain)
    "dcc.annaguidance.ai": "https://api.dcc.annaguidance.ai", // SMC tenant (new domain)
    "demo.annaguidance.ai": "https://api.demo.annaguidance.ai", // SMC tenant (new domain)
    "bctoril.annaguidance.ai": "https://api.bctoril.annaguidance.ai", // BCTORIL tenant
    
    // === STAGING ENVIRONMENTS ===
    "rex.annaguidance.ai": "https://api.rex.annaguidance.ai", // SMC tenant (new domain)
    "staging.annaguidance.ai":
      "https://django-backend-staging-604521917673.asia-northeast1.run.app", // Staging environment
    "rstw.annaguidance.ai": "https://api.rstw.annaguidance.ai", // RSTW staging tenant
    "teknolunas2025.annaguidance.ai": "https://api.teknolunas2025.annaguidance.ai", // RSTW staging tenant
    // Cloud Run staging frontend host -> Django backend
    "react-frontend-staging-604521917673.asia-northeast1.run.app":
      "https://django-backend-staging-604521917673.asia-northeast1.run.app",
    //except this
    "annaguidance.ai":
      "https://django-backend-604521917673.asia-northeast1.run.app", // Public tenant
    "127.0.0.1": "http://127.0.0.1:8000",
    "localhost:3000": "http://localhost:8000",
  };

  const backendURL = tenantMapping[hostname] || "http://localhost:8000";
  return backendURL;
};

// Create Axios instance
const api = axios.create({
  baseURL: getBaseURL(), // Dynamically set the base URL
  headers: {
    "Content-Type": "application/json",
    "X-Tenant-Domain": window.location.hostname, // Dynamically set tenant domain
  },
});

// Where a signed-out visitor belongs: admin pages go to the admin sign-in on this same
// site (never a hard-coded domain, so localhost and staging stay put); student pages go
// to the landing page's sign-in and come back afterwards.
export const loginPathFor = (pathname = window.location.pathname) => {
  if (pathname.startsWith("/admin")) return "/admin/login";
  return "/?login=1";
};

export const clearSession = () => {
  // Notifications stop on this phone (shared phones); needs the token, so it goes first
  turnOffPushOnSignOut();
  localStorage.removeItem("authToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("isAuthenticated");
  // Test answers still waiting for the internet leave with the student (shared phones)
  clearWaitingAnswers();
};

// One forced sign-out per page load: several requests fail together when a session ends.
// (This used to be a sessionStorage flag that only a student sign-in cleared, so after one
// forced sign-out an admin tab skipped every later one and was left half signed in.)
let logoutInProgress = false;

const forceLogout = (message = "Session expired. Please log in again.") => {
  if (logoutInProgress) return;
  logoutInProgress = true;

  clearSession();
  // A counselor's unsent session notes never outlive their sign-in on a shared computer.
  clearAllDrafts();
  toast.error(message);

  const { pathname, search, hash } = window.location;
  const loginPath = loginPathFor(pathname);
  if (loginPath === "/?login=1" && pathname !== "/") {
    sessionStorage.setItem("postLoginRedirect", pathname + search + hash);
  }
  if (pathname === "/admin/login") return;
  window.location.href = loginPath;
};

// Trades the refresh token for a new access token. The server rotates refresh tokens
// (ROTATE_REFRESH_TOKENS), so the new one is stored too; keeping only the access token
// ended every session 24 hours after sign-in however active it was. Requests that hit
// a 401 together share one refresh.
//
// Every tab of this browser shares the one stored session (localStorage), and a rotated
// refresh token works once: the server blacklists it. So when the access token expires,
// every open tab (the chat and the Results page, say) sees a 401 in the same moment and
// each posts the same refresh token; the first wins and the others are turned down for a
// token that was just used, which signed the whole browser out. Two things stop that:
//  - a short lock in localStorage: one tab refreshes while the others wait for the token
//    it writes (the storage event tells them), then carry on with it;
//  - the lock is best effort (localStorage has no atomic test-and-set), so a tab that is
//    turned down anyway re-reads localStorage and keeps the newer token it finds there,
//    waiting a moment for it while the winner is still writing.
// A refresh the server genuinely rejects (the refresh token expired, and no other tab has
// a newer one) still signs out, as before.
const REFRESH_LOCK_KEY = "anna.refreshLock";
// A refresh takes well under this; an older lock was left by a tab closed mid-refresh
const REFRESH_LOCK_MS = 10000;
// How long a tab turned down for a used-up token waits for the winner's token
const LOST_RACE_GRACE_MS = 3000;
const WAIT_POLL_MS = 200;
const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const readLock = () => {
  try {
    const lock = JSON.parse(localStorage.getItem(REFRESH_LOCK_KEY) || "null");
    return lock && typeof lock.tab === "string" && typeof lock.at === "number" ? lock : null;
  } catch {
    return null;
  }
};
const lockedByAnotherTab = () => {
  const lock = readLock();
  return Boolean(lock && lock.tab !== TAB_ID && Date.now() - lock.at < REFRESH_LOCK_MS);
};
// Writes this tab's lock, then checks it is still there: two tabs can write in the same
// moment, and the one whose write was overwritten waits for the other.
const claimLock = () => {
  localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify({ tab: TAB_ID, at: Date.now() }));
  return readLock()?.tab === TAB_ID;
};
const releaseLock = () => {
  if (readLock()?.tab === TAB_ID) localStorage.removeItem(REFRESH_LOCK_KEY);
};
if (typeof window !== "undefined") window.addEventListener("pagehide", releaseLock);

// Resolves with the access token another tab writes (anything but `staleAccess`), or with
// null after `timeoutMs`, or, with `untilUnlocked`, as soon as no other tab holds the lock.
const waitForOtherTab = (staleAccess, { timeoutMs, untilUnlocked }) =>
  new Promise((resolve) => {
    let timer = null;
    let poll = null;
    const finish = (token) => {
      clearTimeout(timer);
      clearInterval(poll);
      window.removeEventListener("storage", check);
      resolve(token);
    };
    const check = () => {
      const token = localStorage.getItem("authToken");
      if (token && token !== staleAccess) finish(token);
      else if (untilUnlocked && !lockedByAnotherTab()) finish(null);
    };
    timer = setTimeout(() => finish(null), timeoutMs);
    poll = setInterval(check, WAIT_POLL_MS);
    window.addEventListener("storage", check);
    check();
  });

// After this tab's refresh was turned down: the token another tab stored meanwhile, or null.
const tokenFromOtherTab = async (staleAccess, usedRefreshToken, error) => {
  const stored = localStorage.getItem("authToken");
  if (stored && stored !== staleAccess) return stored;
  // The other tab signed the browser out: nothing to wait for
  if (!localStorage.getItem("refreshToken")) return null;
  const otherTabRefreshing = lockedByAnotherTab();
  const rotatedMeanwhile = localStorage.getItem("refreshToken") !== usedRefreshToken;
  // simplejwt's answer for a token another refresh already used ("Token is blacklisted")
  const usedUp = /blacklist/i.test(String(error.response?.data?.detail || ""));
  if (!otherTabRefreshing && !rotatedMeanwhile && !usedUp) return null;
  return waitForOtherTab(staleAccess, {
    timeoutMs: otherTabRefreshing ? REFRESH_LOCK_MS : LOST_RACE_GRACE_MS,
    untilUnlocked: otherTabRefreshing,
  });
};

const refreshAcrossTabs = async () => {
  const staleAccess = localStorage.getItem("authToken");
  if (!localStorage.getItem("refreshToken")) throw new Error("Refresh token not found");

  // Another tab is refreshing (or claimed the lock in the same moment): use its token.
  // If it gives up instead (its connection dropped, or it was closed), refresh here.
  if (lockedByAnotherTab() || !claimLock()) {
    const token = await waitForOtherTab(staleAccess, { timeoutMs: REFRESH_LOCK_MS, untilUnlocked: true });
    if (token) return token;
    claimLock();
  }

  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) throw new Error("Refresh token not found"); // signed out while waiting
  try {
    const { data } = await axios.post(
      `${getBaseURL()}/api/token/refresh/`,
      { refresh: refreshToken },
      { headers: { "X-Tenant-Domain": window.location.hostname } },
    );
    // The refresh token first: a tab that sees the new access token then sees this one too
    if (data.refresh) localStorage.setItem("refreshToken", data.refresh);
    localStorage.setItem("authToken", data.access);
    return data.access;
  } catch (error) {
    // Turned down: this tab lost the race if another tab has a newer token by now.
    // No response (the connection dropped) is nothing to recover from here.
    const token = error?.response ? await tokenFromOtherTab(staleAccess, refreshToken, error) : null;
    if (token) return token;
    throw error;
  } finally {
    releaseLock();
  }
};

let refreshing = null;
export const refreshSession = () => {
  if (!refreshing) {
    refreshing = refreshAcrossTabs().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
};

// The access token a request carried, so a 401 can be compared with what is stored now
const bearerToken = (header) =>
  typeof header === "string" && header.startsWith("Bearer ") ? header.slice(7) : null;

// True only when the server turned the token down. A request cut off by a reload or a
// dropped connection has no response, and Safari still runs its error handlers while the
// page reloads after login, so clearing tokens on any error signed iPhone users straight out.
export const isAuthRejected = (error) => [401, 403].includes(error?.response?.status);

// Request Interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add the `X-Tenant-Domain` header to identify the tenant domain
    config.headers["X-Tenant-Domain"] = window.location.hostname;

    // Pages that post to the refresh endpoint themselves (the chat, the landing page, the
    // dashboard) get the shared refresh above instead of a request of their own: one
    // refresh per tab and one tab at a time, the rotated refresh token kept (they stored
    // only the access token, so their next refresh sent a used-up one), and a lost race
    // picked up rather than signed out. They still see a normal 200 with the tokens.
    if ((config.url || "").includes("token/refresh/")) {
      config.adapter = async () => {
        try {
          const access = await refreshSession();
          return {
            data: { access, refresh: localStorage.getItem("refreshToken") },
            status: 200,
            statusText: "OK",
            headers: {},
            config,
            request: null,
          };
        } catch (error) {
          // A rejected refresh is answered as this request's own 401, so the response
          // interceptor below signs out as it always did for a refresh that was turned down
          if (error?.response) throw new axios.AxiosError(error.message, error.code, config, null, error.response);
          throw error;
        }
      };
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response Interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Requests cancelled on purpose (for example a stale filter query) are not errors.
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      const url = originalRequest?.url || "";

      // A refresh made through this client was turned down
      if (url.includes("token/refresh/")) {
        console.error("Token refresh failed:", error);
        forceLogout();
        return Promise.reject(error);
      }

      // Wrong email or password: the sign-in forms say so themselves
      if (url.includes("/auth/token/") || url.includes("/auth/guidance-login/")) {
        return Promise.reject(error);
      }

      // Nobody signed in: nothing to refresh or end
      if (!localStorage.getItem("refreshToken")) {
        return Promise.reject(error);
      }

      // Still turned down with a fresh token (for example a deleted account): sign out.
      // The access token lasts 10 minutes, so a 401 on the first request after a break
      // (often /auth/is-verify/ on page load) is normal and only needs a refresh; signing
      // out there is what sent admins to the student landing page after a reload.
      if (originalRequest._retry) {
        forceLogout("Your session is no longer valid. Please log in again.");
        return Promise.reject(error);
      }
      originalRequest._retry = true;

      try {
        // Another tab of this browser may have refreshed already (one stored session):
        // then the stored token is newer than the one this request carried, and will do.
        const usedToken = bearerToken(originalRequest.headers?.Authorization);
        const storedToken = localStorage.getItem("authToken");
        const newToken =
          storedToken && storedToken !== usedToken ? storedToken : await refreshSession();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        // The server turned the refresh token down (expired). A dropped connection has
        // no response: keep the session for the next try instead of signing out.
        if (refreshError?.response || !localStorage.getItem("refreshToken")) {
          forceLogout();
        }
        return Promise.reject(refreshError);
      }
    } else {
      // Handle other error statuses - but skip login endpoint to avoid duplicates
      if (!error.config?.url?.includes('/auth/guidance-login/')) {
        // Don't show toast errors for normal "not found" responses or expected duplicate checks
        const errorMessage =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.response?.data?.detail;
        const isNormalNotFound = error.response?.status === 404 && (
          errorMessage === "No assessment session found for this user" ||
          errorMessage?.includes("No assessment session found") ||
          error.response?.data?.found === false ||
          error.config?.url?.includes('/get-assessment-session/') ||
          error.config?.url?.includes('/get-assessment-progress/') ||
          error.config?.url?.includes('/get-latest-dass42/') ||
          error.config?.url?.includes('/get-latest-ryff/') ||
          error.config?.url?.includes('/get-latest-sdq/') ||
          error.config?.url?.includes('/get-latest-riasec/') ||
          error.config?.url?.includes('/get-latest-ppca/')
        );
        
        // Treat expired session responses on get-assessment-session as non-errors (no toast)
        const isExpiredSession = (
          error.config?.url?.includes('/get-assessment-session/') && (
            error.response?.status === 410 ||
            error.response?.data?.expired === true ||
            /expired/i.test(errorMessage || '')
          )
        );
        
        // Don't show toast errors for expected duplicate check 400 responses
        const isDuplicateCheck = error.response?.status === 400 &&
          error.config?.url?.includes('/users/check-duplicate/');

        // Don't show toast for booking endpoints — they have their own specific error handling
        const isBookingError = error.config?.url?.includes('/api/booking/appointments/');

        // Don't show toast for non-critical analytics tracking
        const isAnalyticsError = error.config?.url?.includes('/api/track-course-click/');

        // Feedback forms display their own field-level submission errors.
        const isFeedbackError = error.config?.url?.includes('/api/submit-feedback/')
          || error.config?.url?.includes('/api/check-feedback/');

        // The review and release page shows its own errors next to the action that failed.
        const isAssessmentReviewError = error.config?.url?.includes('/assessment-ai/')
          && !error.config?.url?.includes('/assessment-ai/release-status/');

        // Callers that show their own inline error can opt out with { skipErrorToast: true }.
        const isSilenced = Boolean(error.config?.skipErrorToast);

        if (!isNormalNotFound && !isDuplicateCheck && !isExpiredSession && !isBookingError && !isAnalyticsError && !isFeedbackError && !isAssessmentReviewError && !isSilenced) {
          toast.error(
            errorMessage || "An error occurred. Please try again.",
          );
        }
      }
      return Promise.reject(error);
    }
  },
);

export default api;

// Track course click for analytics
export const trackCourseClick = async (
  courseName,
  category = "riasec",
  sessionId = null,
  riasecType = null,
) => {
  try {
    const token = localStorage.getItem("authToken");
    if (!token) {
      console.warn("No auth token found, skipping course click tracking");
      return;
    }

    const response = await api.post(
      "/api/track-course-click/",
      {
        course_name: courseName,
        category: category,
        session_id: sessionId,
        riasec_type: riasecType,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    console.log("Course click tracked successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error tracking course click:", error);
    // Don't throw error to avoid disrupting user experience
    return null;
  }
};

// Get popular courses for dashboard
export const getPopularCourses = async (filters = {}) => {
  try {
    const token = localStorage.getItem("authToken");
    if (!token) {
      throw new Error("No auth token found");
    }

    // Normalize helper: treat 'Overall' or falsy as no filter
    const isReal = (v) => v !== undefined && v !== null && `${v}`.trim() !== "" && `${v}`.toLowerCase() !== "overall" && `${v}`.toLowerCase() !== "all";

    const queryParams = new URLSearchParams();

    if (isReal(filters.strand)) {
      queryParams.append("strand", filters.strand);
    }

    const gradeParam = (filters.grade_level ?? filters.gradeLevel);
    if (isReal(gradeParam)) {
      queryParams.append("grade_level", gradeParam);
    }

    if (isReal(filters.section)) {
      queryParams.append("section", filters.section);
    }

    // Forward timeframe filters when provided
    if (filters.use_date_range && isReal(filters.start_date) && isReal(filters.end_date)) {
      queryParams.append("use_date_range", "true");
      queryParams.append("start_date", filters.start_date);
      queryParams.append("end_date", filters.end_date);
    } else if (isReal(filters.schoolYear)) {
      queryParams.append("school_year", filters.schoolYear);
    }

    // Optional: category and days
    if (isReal(filters.category)) {
      queryParams.append("category", filters.category);
    }
    if (isReal(filters.days)) {
      queryParams.append("days", `${filters.days}`);
    }
    
    // Department filters - these were missing!
    if (isReal(filters.department_type)) {
      queryParams.append("department_type", filters.department_type);
    }
    if (isReal(filters.organizational_level_id)) {
      queryParams.append("organizational_level_id", filters.organizational_level_id);
    }
    if (isReal(filters.section_id)) {
      queryParams.append("section_id", filters.section_id);
    }

    const url = `/api/popular-courses/${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    const response = await api.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching popular courses:", error);
    throw error;
  }
};

// Check if a set of course names are offered at the current school (public schema)
export const getOfferedCoursesMap = async (courseNames = [], schoolYear = null) => {
  if (!Array.isArray(courseNames) || courseNames.length === 0) return {};
  try {
    // Get current school's offerings directly
    const offeringsResp = await api.get('/api/offerings/');
    const currentOfferings = offeringsResp.data || [];
    
    console.log('🏫 getOfferedCoursesMap - Input courseNames:', courseNames);
    console.log('🏫 getOfferedCoursesMap - API offerings response:', currentOfferings);
    
    const map = {};
    
    // For each course name, check if it's in the current school's offerings
    courseNames.forEach(courseName => {
      const normalizedName = courseName.trim().toLowerCase();
      
      // Find matching offering
      const matchingOffering = currentOfferings.find(offering => {
        const offeringName = (offering.course_name || '').trim().toLowerCase();
        return offeringName === normalizedName;
      });
      
      const isOffered = matchingOffering && matchingOffering.is_offered !== false;
      
      // Log details for nursing specifically
      if (courseName.toLowerCase().includes('nursing')) {
        console.log('🩺 NURSING COURSE DEBUG:', {
          originalCourseName: courseName,
          normalizedName: normalizedName,
          matchingOffering: matchingOffering,
          isOffered: isOffered,
          allOfferingNames: currentOfferings.map(o => o.course_name)
        });
      }
      
      map[normalizedName] = isOffered;
    });
    
    console.log('🗺️ Final offered map:', map);
    return map;
  } catch (error) {
    console.error('❌ getOfferedCoursesMap error:', error);
    return {};
  }
};
